import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { twJoin, twMerge } from '@/utils/tw-merge';

const textButtonVariants = cva(
  'disabled:cursor-not-allowed disabled:text-gray-600',
  {
    variants: {
      variant: {
        primary: [
          'text-blue-500',
          'underline hover:no-underline',
          'active:text-black [&.active]:text-black',
          'focus-visible:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        ].join(' '),
        black: [
          'text-black',
          'underline hover:no-underline',
          'focus-visible:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        ].join(' '),
        gray: [
          'text-gray-800',
          'hover:text-blue-500',
          'focus-visible:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        ].join(' '),
      },
      size: {
        medium: 'style-link',
        small: 'style-link-small',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

export interface TextButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof textButtonVariants> {
  asChild?: boolean;
  active?: boolean;
}

export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    { className, size, children, variant, asChild = false, active, ...props },
    ref
  ) => {
    const Component = asChild ? Slot : 'button';

    return (
      <Component
        className={twJoin(
          twMerge(textButtonVariants({ variant, size }), className),
          active && 'active'
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
TextButton.displayName = 'TextButton';
